<template>
  <div class="committee-dashboard">
    <div v-if="!embedded" class="dashboard-page-actions">
      <span class="dashboard-page-actions__meta">{{ $t('common.lastUpdated') }}: {{ formatRefreshTime(lastUpdatedAt) }}</span>
      <CButton color="primary" class="font-weight-bold" size="sm" :disabled="dashboardLoading" @click="refreshDashboard">
        {{ $t('common.refresh') }}
      </CButton>
    </div>

    <CCard v-if="!embedded" class="dashboard-hero border-0 shadow-sm mb-4">
      <CCardBody class="p-4 p-lg-5">
        <div class="hero-kicker">{{ $t('pages.dashboard.committee.heroKicker') }}</div>
        <h2 class="hero-title">{{ $t('pages.dashboard.committee.heroTitle') }}</h2>
        <p class="hero-copy">
          {{ $t('pages.dashboard.committee.heroCopy') }}
        </p>
        <div class="hero-actions">
          <CButton color="primary" class="font-weight-bold mr-2 mb-2" to="/review">
            {{ $t('pages.dashboard.committee.openReviewPage') }}
          </CButton>
          <CButton color="secondary" variant="outline" class="mr-2 mb-2" to="/review-history">
            {{ $t('pages.dashboard.committee.reviewHistory') }}
          </CButton>
          <CButton color="secondary" variant="outline" class="mb-2" to="/guideline">
            {{ $t('pages.dashboard.committee.guideline') }}
          </CButton>
        </div>
      </CCardBody>
    </CCard>

    <CCard v-if="deadlineReminder" class="deadline-reminder-card border-0 shadow-sm mb-4">
      <CCardBody class="p-4">
        <div class="deadline-reminder">
          <div class="deadline-reminder__copy">
            <div class="deadline-reminder__eyebrow">{{ $t('pages.dashboard.committee.deadlineEyebrow') }}</div>
            <div class="deadline-reminder__title">{{ deadlineReminder.title }}</div>
            <div class="deadline-reminder__text">{{ deadlineReminder.text }}</div>
          </div>
          <div class="deadline-reminder__actions">
            <span class="deadline-reminder__badge">{{ deadlineReminder.badge }}</span>
            <CButton color="primary" size="sm" @click="openReview(deadlineReminder.review)">
              {{ $t('pages.dashboard.committee.openReview') }}
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CRow class="mb-2">
      <CCol md="6" xl="3" v-for="item in summaryStats" :key="item.label" class="mb-4">
        <CCard class="stat-card border-0 shadow-sm h-100" @click.native="goTo(item.to)">
          <CCardBody>
            <div class="stat-header">
              <div class="stat-label">{{ item.label }}</div>
              <div class="stat-icon" :class="item.iconClass">{{ item.icon }}</div>
            </div>
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-delta">{{ item.delta }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="8" class="mb-4">
        <CCard class="panel-card panel-card--chart border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.committee.panelActivityTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.committee.panelActivitySubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="chart-card-body pt-0">
            <div class="chart-insights">
              <div class="chart-insights__headline">{{ $t('pages.dashboard.committee.chartHeadline') }}</div>
              <CChartBar
                :datasets="activityChart.datasets"
                :labels="activityChart.labels"
                :options="activityChartOptions"
              />
              <div class="chart-insights__grid">
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ $t('pages.dashboard.committee.currentFocus') }}</span>
                  <strong class="chart-insights__value">{{ $t('pages.dashboard.committee.currentFocusValue', { count: statsData.inProgress }) }}</strong>
                </div>
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ $t('pages.dashboard.committee.dueSoon') }}</span>
                  <strong class="chart-insights__value">{{ $t('pages.dashboard.committee.dueSoonValue', { count: statsData.dueSoon }) }}</strong>
                </div>
                <div class="chart-insights__item">
                  <span class="chart-insights__label">{{ $t('pages.dashboard.committee.completedThisWeek') }}</span>
                  <strong class="chart-insights__value">{{ $t('pages.dashboard.committee.completedThisWeekValue', { count: statsData.completedThisWeek }) }}</strong>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.committee.quickActionsTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.committee.quickActionsSubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <button v-for="item in quickActions" :key="item.label" class="quick-action" @click="goTo(item.to)">
              <span class="quick-action__icon">{{ item.icon }}</span>
              <span class="quick-action__content">
                <span class="quick-action__label">{{ item.label }}</span>
                <span class="quick-action__copy">{{ item.copy }}</span>
              </span>
              <span class="quick-action__badge">{{ item.badge }}</span>
            </button>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol lg="12" class="mb-4">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0">
            <div>
              <div class="panel-title">{{ $t('pages.dashboard.committee.queueTitle') }}</div>
              <div class="panel-subtitle">{{ $t('pages.dashboard.committee.queueSubtitle') }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div v-if="queueItems.length">
              <div v-for="item in queueItems" :key="item.id" class="queue-item">
                <div class="queue-item__content">
                  <div class="queue-item__title">{{ item.agreement }}</div>
                  <div class="queue-item__meta">
                    {{ item.partner }} · {{ item.type }} · {{ formatStatus(item.status) }} · {{ formatDaysLeft(item.daysLeft) }}
                  </div>
                  <div v-if="item.priority === 'high'" class="queue-item__flag">
                    {{ $t('pages.dashboard.committee.highPriorityDeadline') }}
                  </div>
                </div>
                <CButton size="sm" color="secondary" variant="outline" @click="openReview(item)">
                  {{ $t('pages.dashboard.committee.open') }}
                </CButton>
              </div>
            </div>
            <div v-else class="empty-copy">{{ $t('pages.dashboard.committee.noQueueItems') }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol md="6" xl="4" class="mb-4" v-for="card in insightCards" :key="card.title">
        <CCard class="panel-card border-0 shadow-sm h-100">
          <CCardHeader class="panel-header bg-white border-0 pb-2">
            <div>
              <div class="panel-title panel-title--small">{{ card.title }}</div>
              <div class="panel-subtitle">{{ card.subtitle }}</div>
            </div>
          </CCardHeader>
          <CCardBody class="pt-0">
            <div class="data-list">
              <div v-for="item in card.items" :key="item.label" class="data-row">
                <span class="data-row__label">{{ item.label }}</span>
                <span class="data-row__value">{{ item.value }}</span>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { CChartBar } from '@coreui/vue-chartjs'
import Service from '@/service/api'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { formatTimeWithPreferences } from '@/utils/workspace-preferences'

const COMMITTEE_DASHBOARD_CACHE_KEY = 'committee-dashboard-page'
const COMMITTEE_DASHBOARD_CACHE_MAX_AGE = 5 * 60 * 1000

function normalizeReviewRows(value) {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object') {
    return Object.values(value).filter(Boolean)
  }
  return []
}

export default {
  name: 'CommitteeDashboard',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CChartBar
  },
  data() {
    return {
      statsData: {
        pending: 0,
        inProgress: 0,
        sentToAdmin: 0,
        completed: 0,
        dueSoon: 0,
        startedToday: 0,
        completedThisWeek: 0,
        averageTime: 0
      },
      reviews: [],
      lastUpdatedAt: '',
      dashboardLoading: false,
      activityChartOptions: {
        maintainAspectRatio: false,
        responsive: true,
        legend: {
          position: 'bottom'
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0
            }
          }]
        }
      }
    }
  },
  computed: {
    inReviewCount() {
      return Number(this.statsData.pending || 0) + Number(this.statsData.inProgress || 0)
    },
    totalReviewCount() {
      return Number(this.statsData.pending || 0) + Number(this.statsData.inProgress || 0) + Number(this.statsData.completed || 0)
    },
    completionRate() {
      if (!this.totalReviewCount) return 0
      return Math.round((Number(this.statsData.completed || 0) / this.totalReviewCount) * 100)
    },
    activityChart() {
      return {
        labels: [
          this.$t('pages.dashboard.committee.inReviewChart'),
          this.$t('pages.dashboard.committee.sentToAdminChart'),
          this.$t('pages.dashboard.committee.completedChart')
        ],
        datasets: [
          {
            label: this.$t('pages.dashboard.committee.committeeReviewsDataset'),
            data: [
              this.inReviewCount,
              Number(this.statsData.sentToAdmin || 0),
              Number(this.statsData.completed || 0)
            ],
            backgroundColor: [
              'rgba(245, 158, 11, 0.82)',
              'rgba(15, 108, 189, 0.8)',
              'rgba(22, 163, 74, 0.78)'
            ],
            borderColor: ['#d97706', '#0f6cbd', '#15803d'],
            borderRadius: 12,
            borderWidth: 1,
            maxBarThickness: 60
          }
        ]
      }
    },
    urgentReviews() {
      return this.reviews.filter(item => item.daysLeft <= 3 && item.status !== 'completed')
    },
    deadlineReminder() {
      const item = this.urgentReviews[0]
      if (!item) return null
      return {
        review: item,
        title: this.$t('pages.dashboard.committee.highPriorityTitle', { agreement: item.agreement }),
        text: this.$t('pages.dashboard.committee.highPriorityText', {
          timeRemaining: this.formatDaysLeft(item.daysLeft).toLowerCase()
        }),
        badge: item.daysLeft <= 0
          ? this.$t('pages.dashboard.committee.dueTodayBadge')
          : this.$t('pages.dashboard.committee.daysLeftBadge', { count: item.daysLeft })
      }
    },
    queueItems() {
      return this.reviews
        .filter(item => item.status !== 'completed')
        .slice(0, 5)
    },
    summaryStats() {
      return [
        {
          label: this.$t('pages.dashboard.committee.inProgress'),
          value: this.inReviewCount,
          delta: this.$t('pages.dashboard.committee.pendingReviewDelta', { count: this.statsData.dueSoon }),
          to: '/review',
          icon: 'P',
          iconClass: 'stat-icon--warning'
        },
        {
          label: this.$t('pages.dashboard.committee.sentToAdmin'),
          value: Number(this.statsData.sentToAdmin || 0),
          delta: this.$t('pages.dashboard.committee.sentToAdminDelta'),
          to: '/review',
          icon: 'S',
          iconClass: 'stat-icon--active'
        },
        {
          label: this.$t('pages.dashboard.committee.completed'),
          value: this.statsData.completed,
          delta: this.$t('pages.dashboard.committee.completedDelta', { count: this.statsData.completedThisWeek }),
          to: '/review-history',
          icon: 'C',
          iconClass: 'stat-icon--success'
        },
        {
          label: this.$t('pages.dashboard.committee.averageReviewTime'),
          value: `${this.statsData.averageTime}d`,
          delta: this.$t('pages.dashboard.committee.averageReviewTimeDelta'),
          to: '/review-history',
          icon: 'T',
          iconClass: 'stat-icon--neutral'
        }
      ]
    },
    insightCards() {
      const mostUrgent = this.urgentReviews[0]
      const activeItem = this.reviews.find(item => item.status === 'in-progress')
      return [
        {
          title: this.$t('pages.dashboard.committee.workloadBreakdown'),
          subtitle: this.$t('pages.dashboard.committee.workloadBreakdownSubtitle'),
          items: [
            { label: this.$t('pages.dashboard.committee.inProgress'), value: this.inReviewCount },
            { label: this.$t('pages.dashboard.committee.sentToAdmin'), value: Number(this.statsData.sentToAdmin || 0) },
            { label: this.$t('pages.dashboard.committee.completed'), value: this.statsData.completed }
          ]
        },
        {
          title: this.$t('pages.dashboard.committee.deadlinePressure'),
          subtitle: this.$t('pages.dashboard.committee.deadlinePressureSubtitle'),
          items: [
            { label: this.$t('pages.dashboard.committee.dueSoon'), value: this.statsData.dueSoon },
            { label: this.$t('pages.dashboard.committee.topUrgentItem'), value: mostUrgent ? mostUrgent.agreement : this.$t('pages.dashboard.committee.noUrgentItems') },
            { label: this.$t('pages.dashboard.committee.daysLeft'), value: mostUrgent ? this.$t('pages.dashboard.committee.daysLeftValue', { count: mostUrgent.daysLeft }) : this.$t('pages.dashboard.committee.stable') }
          ]
        },
        {
          title: this.$t('pages.dashboard.committee.currentFocusCard'),
          subtitle: this.$t('pages.dashboard.committee.currentFocusCardSubtitle'),
          items: [
            { label: this.$t('pages.dashboard.committee.startedToday'), value: this.statsData.startedToday },
            { label: this.$t('pages.dashboard.committee.averageReviewTime'), value: `${this.statsData.averageTime}d` },
            { label: this.$t('pages.dashboard.committee.activeAgreement'), value: activeItem ? activeItem.agreement : this.$t('pages.dashboard.committee.noActiveReview') }
          ]
        }
      ]
    },
    quickActions() {
      return [
        {
          label: this.$t('pages.dashboard.committee.openReviewPage'),
          copy: this.$t('pages.dashboard.committee.openReviewPageCopy'),
          badge: this.statsData.pending + this.statsData.inProgress,
          icon: 'R',
          to: '/review'
        },
        {
          label: this.$t('pages.dashboard.committee.reviewHistory'),
          copy: this.$t('pages.dashboard.committee.reviewHistoryCopy'),
          badge: this.statsData.completed,
          icon: 'H',
          to: '/review-history'
        },
        {
          label: this.$t('pages.dashboard.committee.guideline'),
          copy: this.$t('pages.dashboard.committee.guidelineCopy'),
          badge: 0,
          icon: 'G',
          to: '/guideline'
        }
      ]
    }
  },
  mounted() {
    this.loadCachedDashboard()
    this.loadDashboard()
  },
  methods: {
    applyDashboardData(data) {
      const payload = data && typeof data === 'object' ? data : {}
      this.statsData = Object.assign({}, this.statsData, payload.stats || {})
      this.reviews = normalizeReviewRows(payload.reviews)
    },
    loadCachedDashboard() {
      const cached = readPageCache(COMMITTEE_DASHBOARD_CACHE_KEY, COMMITTEE_DASHBOARD_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.applyDashboardData(cached)
      this.lastUpdatedAt = cached.lastUpdatedAt || ''
    },
    async loadDashboard(options = {}) {
      if (this.dashboardLoading) return
      this.dashboardLoading = true
      try {
        const response = await Service.committeeReview('dashboard', null, {
          forceRefresh: !!options.forceRefresh
        })
        const data = response && response.data && response.data.data ? response.data.data : {}
        this.applyDashboardData(data)
        this.lastUpdatedAt = new Date().toISOString()
        writePageCache(COMMITTEE_DASHBOARD_CACHE_KEY, {
          stats: this.statsData,
          reviews: this.reviews,
          lastUpdatedAt: this.lastUpdatedAt
        })
      } catch (error) {
        if (!this.lastUpdatedAt) {
          this.reviews = []
        }
      } finally {
        this.dashboardLoading = false
      }
    },
    refreshDashboard() {
      return this.loadDashboard({ forceRefresh: true })
    },
    formatRefreshTime(value) {
      if (!value) return this.$t('pages.dashboard.committee.justNow')
      return formatTimeWithPreferences(value, {
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    formatStatus(status) {
      return {
        pending: this.$t('pages.dashboard.committee.statusPending'),
        'in-progress': this.$t('pages.dashboard.committee.statusInProgress'),
        completed: this.$t('pages.dashboard.committee.statusCompleted')
      }[status] || status
    },
    formatDaysLeft(daysLeft) {
      const value = Number(daysLeft || 0)
      if (value <= 0) return this.$t('pages.dashboard.committee.dueToday')
      if (value === 1) return this.$t('pages.dashboard.committee.oneDayLeft')
      return this.$t('pages.dashboard.committee.daysLeftValue', { count: value })
    },
    goTo(path) {
      if (!path) return
      this.$router.push(path)
    },
    openReview(review) {
      if (!review) return
      this.$router.push({ path: '/review', query: { id: review.agreementId || review.id } })
    }
  }
}
</script>

<style scoped>
.dashboard-page-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  margin-bottom: 10px;
  padding: 0 2px;
}

.dashboard-page-actions__meta {
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  padding: 9px 12px;
}

.dashboard-hero {
  border-radius: 1.5rem !important;
  border: 0 !important;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(44, 52, 71, 0.1) !important;
  background:
    radial-gradient(circle at top right, rgba(254, 194, 96, 0.28), transparent 28%),
    radial-gradient(circle at left center, rgba(255, 255, 255, 0.12), transparent 26%),
    linear-gradient(135deg, #5d0f12 0%, #8c1515 46%, #c77f28 100%) !important;
}

.hero-kicker {
  color: rgba(255, 248, 240, 0.8);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.hero-title {
  color: #fff8f1;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 0.85rem;
  max-width: 42rem;
}

.hero-copy {
  color: rgba(255, 246, 237, 0.86);
  font-size: 1rem;
  margin-bottom: 1.25rem;
  max-width: 40rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
}

.stat-card {
  border-radius: 1.25rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.stat-card:hover {
  box-shadow: 0 18px 34px rgba(44, 52, 71, 0.12);
  transform: translateY(-2px);
}

.stat-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.stat-label {
  color: #5c6f82;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stat-icon {
  align-items: center;
  border-radius: 18px;
  display: inline-flex;
  font-size: 1.1rem;
  font-weight: 700;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.stat-icon--warning {
  background: rgba(245, 158, 11, 0.16);
  color: #b45309;
}

.stat-icon--active {
  background: rgba(16, 185, 129, 0.16);
  color: #047857;
}

.stat-icon--success {
  background: rgba(34, 197, 94, 0.14);
  color: #15803d;
}

.stat-icon--neutral {
  background: rgba(148, 163, 184, 0.16);
  color: #475569;
}

.stat-value {
  color: #243b53;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.05;
}

.stat-delta {
  color: #64748b;
  margin-top: 0.55rem;
}

.panel-card {
  border-radius: 1.25rem;
  overflow: hidden;
}

.deadline-reminder-card {
  background:
    radial-gradient(circle at top right, rgba(254, 202, 202, 0.55), transparent 28%),
    linear-gradient(135deg, #fff7ed 0%, #fff1f2 100%);
  border-left: 6px solid #dc2626 !important;
  border-radius: 1.25rem !important;
}

.deadline-reminder {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.deadline-reminder__eyebrow {
  color: #b42318;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.deadline-reminder__title {
  color: #7f1d1d;
  font-size: 1.2rem;
  font-weight: 800;
}

.deadline-reminder__text {
  color: #7c2d12;
  margin-top: 4px;
  max-width: 760px;
}

.deadline-reminder__actions {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.deadline-reminder__badge {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  border-radius: 999px;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
  padding: 8px 12px;
  text-transform: uppercase;
}

.panel-header {
  padding: 1.2rem 1.35rem 0.8rem;
}

.panel-title {
  color: #243b53;
  font-size: 1rem;
  font-weight: 700;
}

.panel-title--small {
  font-size: 0.95rem;
}

.chart-card-body {
  min-height: 210px;
}

.panel-card--chart {
  height: auto;
}

.panel-subtitle {
  color: #7b8794;
  font-size: 0.92rem;
  margin-top: 0.2rem;
}

.quick-action {
  align-items: center;
  background: #f8fbff;
  border: 1px solid #dce8f5;
  border-radius: 1rem;
  cursor: pointer;
  display: flex;
  gap: 0.9rem;
  margin-bottom: 0.9rem;
  padding: 0.95rem 1rem;
  text-align: left;
  width: 100%;
}

.quick-action__icon {
  align-items: center;
  background: #e8f1fb;
  border-radius: 14px;
  color: #0f6cbd;
  display: inline-flex;
  font-weight: 700;
  height: 2.6rem;
  justify-content: center;
  width: 2.6rem;
}

.quick-action__content {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.quick-action__label {
  color: #243b53;
  font-weight: 700;
}

.quick-action__copy {
  color: #6b7280;
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.quick-action__badge {
  background: #fff;
  border: 1px solid #dce8f5;
  border-radius: 999px;
  color: #0f6cbd;
  font-size: 0.85rem;
  font-weight: 700;
  min-width: 2.2rem;
  padding: 0.3rem 0.65rem;
  text-align: center;
}

.chart-insights {
  padding-top: 0.25rem;
}

.chart-insights__headline {
  color: #52606d;
  font-size: 0.95rem;
  line-height: 1.5;
}

.chart-insights__grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 1rem;
}

.chart-insights__item {
  background: #f8fbff;
  border: 1px solid #dce8f5;
  border-radius: 1rem;
  padding: 0.95rem 1rem;
}

.chart-insights__label {
  color: #64748b;
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.chart-insights__value {
  color: #243b53;
  display: block;
  font-size: 1rem;
  margin-top: 0.45rem;
}

.queue-item {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0.95rem 0;
}

.queue-item + .queue-item {
  border-top: 1px solid #eef2f7;
}

.queue-item__title {
  color: #243b53;
  font-weight: 700;
}

.queue-item__meta {
  color: #6b7280;
  margin-top: 0.2rem;
}

.data-list {
  display: grid;
  gap: 0.9rem;
}

.data-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.data-row__label {
  color: #6b7280;
}

.data-row__value {
  color: #243b53;
  font-weight: 700;
}

.empty-copy {
  color: #94a3b8;
}

@media (max-width: 991px) {
  .chart-insights__grid {
    grid-template-columns: 1fr;
  }
}
</style>
.queue-item__flag {
  color: #b42318;
  font-size: 0.8rem;
  font-weight: 800;
  margin-top: 4px;
  text-transform: uppercase;
}

@media (max-width: 767px) {
  .deadline-reminder {
    align-items: flex-start;
    flex-direction: column;
  }

  .deadline-reminder__actions {
    align-items: flex-start;
  }
}
